/**
 * V2 pilot: four meaningful activities, not a question-per-screen slideshow.
 * Pure, read-only selectors over the EXISTING saved Journey fields.
 * This module must never manufacture experimental results or alter completion gates.
 */
export const FLOOD_FAIR_TEST_ACTIVITIES = [
  {
    id: "evidence",
    title: "Find your starting evidence",
    purpose: "Recall what happened to Bridge 1 and what you changed in Bridge 2.",
    instruction: "Find your real Bridge 1 test and your Bridge 2 design. If either is missing, go back and record it before comparing.",
  },
  {
    id: "fairness",
    title: "Prepare a fair test",
    purpose: "Make sure you're comparing the bridge designs, not two different experiments.",
    instruction: "Watch the short fair-test video, check the physical setup, and confirm what you kept the same.",
  },
  {
    id: "test",
    title: "Test and record Bridge 2",
    purpose: "Run your redesign and collect real evidence.",
    instruction: "Add the same loading objects, stop when the bridge fails, and record what you actually observed—even if it held less.",
  },
  {
    id: "compare",
    title: "What does the evidence show?",
    purpose: "Explain whether your design change helped using both tests.",
    instruction: "Compare your two recorded capacities and observations. Tell what the evidence supports; don't guess or change the results.",
  },
];

const nonblank = (value, length = 1) =>
  String(value ?? "").trim().length >= length;
const hasAllKeys = (value, keys) =>
  !!value && typeof value === "object" && keys.every((key) => value[key] === "complete");

export const getFloodFairTestStatus = (sessionState) => {
  const steps = sessionState?.steps || {};
  const bridge1 = steps["flat-bridge-test"]?.bridge1_record;
  const bridge2Plan = steps["engineer-bridge-two"] || {};
  const fairTest = steps["fair-rescue-test"] || {};
  const bridge2 = fairTest.bridge2_record;

  const bridge1Recorded = !!bridge1 &&
    nonblank(bridge1.capacity) && nonblank(bridge1.failure_location) &&
    nonblank(bridge1.failure_observation, 8);
  const bridge2Planned = nonblank(bridge2Plan.bridge2_design_change, 8) &&
    nonblank(bridge2Plan.bridge2_design_prediction, 15);
  const fairSetupConfirmed = hasAllKeys(fairTest.fair_test_constants, [
    "same_spacing", "same_paper", "same_load", "same_position", "same_method",
  ]);
  const bridge2Recorded = !!bridge2 &&
    nonblank(bridge2.capacity) && nonblank(bridge2.failure_location) &&
    nonblank(bridge2.observation, 8);
  const explained = nonblank(fairTest.bridge_comparison_claim, 20);
  const completed = [bridge1Recorded && bridge2Planned, fairSetupConfirmed,
    bridge2Recorded, explained];
  const firstIncompleteIndex = completed.findIndex((item) => !item);
  return {
    bridge1,
    bridge1Recorded,
    bridge2Plan,
    bridge2Planned,
    bridge2,
    fairSetupConfirmed,
    bridge2Recorded,
    explained,
    completed,
    firstIncompleteIndex: firstIncompleteIndex < 0 ? 3 : firstIncompleteIndex,
    // Later activities remain locked until the real prerequisite is complete.
    maxUnlockedIndex: firstIncompleteIndex < 0 ? 3 : firstIncompleteIndex,
  };
};

export const isFloodActivityFirstFlagEnabled = () => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("ff_swan_unified_panel") === "1" ||
      /^\/wonderlabs(?:\/|$)/.test(window.location.pathname);
  } catch (_error) {
    return false;
  }
};
